import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import { useAddPaymentMethod, useSubscriptionStatus } from '@hooks';
import { root_path } from '@routes';
import { useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import { trackSubscriptionInGaAndAhoy } from '@tracking';
import { extractQueryParams } from '@utils/queryStringHelper';

import styles from './styles.module.scss';

enum StripeStatus {
  Succeeded = 'succeeded',
  Processing = 'processing',
  RequiresPaymentMethod = 'requires_payment_method',
}

type Status =
  | 'canceled'
  | 'processing'
  | 'requires_action'
  | 'requires_capture'
  | 'requires_confirmation'
  | 'requires_payment_method'
  | 'succeeded';

const PaymentStatus = () => {
  const {
    gift_offer_code: giftOfferCode,
    gift_recipient_stripe_customer_id: giftRecipientStripeCustomerId,
    payment_intent_client_secret: paymentIntentClientSecret,
    pause_subscription: isPauseSubscription,
    referer,
    setup_intent_client_secret: setupIntentClientSecret,
    resume_subscription: isResumeSubscription,
    update_payment: isUpdatePayment,
  } = extractQueryParams([
    'gift_offer_code',
    'gift_recipient_stripe_customer_id',
    'payment_intent_client_secret',
    'pause_subscription',
    'referer',
    'resume_subscription',
    'setup_intent_client_secret',
    'update_payment',
  ]);
  const stripe = useStripe();
  const [message, setMessage] = useState(null);
  const [subMessage, setSubMessage] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const { data, refetch, isError } = useSubscriptionStatus(
    {
      referer,
      ...(giftRecipientStripeCustomerId && {
        gift_recipient_stripe_customer_id: giftRecipientStripeCustomerId,
      }),
      ...(giftOfferCode && {
        gift_offer_code: giftOfferCode,
      }),
      ...(isResumeSubscription && {
        resuming_subscription: true,
      }),
      ...(isPauseSubscription && {
        is_pause_subscription: true,
      }),
    },
    !isUpdatePayment
  );
  const { mutate: addPaymentMethod } = useAddPaymentMethod();

  const handlePaymentUpdate = useCallback(
    (status: Status, error: StripeError) => {
      if (error) {
        toast.error('Something went wrong, please try again');
        window.location.href = root_path();
        return;
      }

      switch (status) {
        case StripeStatus.Succeeded:
          setMessage('Confirming payment method.');
          setSubMessage('Please hold while your payment is being processed.');
          addPaymentMethod();
          break;

        case StripeStatus.RequiresPaymentMethod:
          // Redirect your user back to your payment page to attempt collecting payment again
          setMessage('Payment method failed.');
          setSubMessage('Please hold while we redirect you back to try another payment method.');
          setTimeout(() => {
            history.back();
          }, 5000);
          break;
      }
    },
    [addPaymentMethod]
  );

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const userMissingPaymentIntent =
      !setupIntentClientSecret &&
      !paymentIntentClientSecret &&
      !isResumeSubscription &&
      !isPauseSubscription;
    if (userMissingPaymentIntent) {
      toast.error("You don't have permission to access this page.");
      window.location.href = root_path;
      return;
    }

    // If this is a payment method collection or update, we need to retrieve the setup intent and confirm it
    // else if this is a new subscription, we need to retrieve the subscription status
    if (setupIntentClientSecret && isUpdatePayment) {
      stripe.retrieveSetupIntent(setupIntentClientSecret).then(({ setupIntent, error }) => {
        handlePaymentUpdate(setupIntent.status, error);
      });
    } else if (paymentIntentClientSecret || isResumeSubscription || isPauseSubscription) {
      if (isPauseSubscription) {
        setMessage('Confirming your subscription.');
        setSubMessage('Please hold while your subscription is being paused.');
      } else if (isResumeSubscription) {
        setMessage('Confirming your subscription.');
        setSubMessage('Please hold while your subscription is being resumed.');
      } else if (giftRecipientStripeCustomerId) {
        setMessage('Confirming your payment.');
        setSubMessage('Please hold while your gift subscription is being processed.');
      } else {
        setMessage('Confirming your subscription.');
        setSubMessage('Please hold while your subscription is being processed.');
      }
      const interval = setInterval(() => {
        if (elapsed >= 30000 || isError) {
          if (isPauseSubscription) {
            toast.error(
              'Pausing your subscription is taking longer than expected. Please wait a few minutes.'
            );
          } else {
            toast.error(
              'Your subscription activation is taking longer than expected. Please wait a few minutes.'
            );
          }
          clearInterval(interval);
          setTimeout(() => {
            window.location.href = root_path;
          }, 3000);
        } else if (data?.success) {
          if (isPauseSubscription) {
            window.location.href = data.redirectTo;
          } else {
            trackSubscriptionInGaAndAhoy(data);
          }
          clearInterval(interval);
        } else {
          refetch();
        }
        setElapsed((prevElapsed) => prevElapsed + 5000);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [
    giftRecipientStripeCustomerId,
    data,
    elapsed,
    handlePaymentUpdate,
    isError,
    isUpdatePayment,
    paymentIntentClientSecret,
    isPauseSubscription,
    refetch,
    setupIntentClientSecret,
    stripe,
    addPaymentMethod,
    isResumeSubscription,
  ]);

  return (
    <div id="payment-status" className={clsx(styles.confirmationContainer, 'container')}>
      <div className="row">
        <div className="col-md-10 col-md-offset-1">
          <div className={styles.confirmationCard}>
            <div className={styles.loader}>
              <CustomSpinner color={CustomSpinnerColor.PrimaryMagenta} size={15} />
            </div>
            <div className={styles.message}>{message}</div>
            <div className={styles.subMessage}>{subMessage}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
